import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CapabilityGate } from '../../../../components/CapabilityGate';
import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { useAuthStore } from '../../../../stores/auth-store';
import { useToastStore } from '../../../../stores/toast-store';
import {
  AcceptAlertWithJustificationModal,
  ConflictAlertCard,
  ConflictAlertHistoryRow,
  FalsePositiveModal,
  MitigateAlertModal,
  PageHead,
  ReopenAlertModal,
  ResolveModal,
} from '../../components';
import {
  useMitigateConflictAlert,
  useAcceptConflictAlert,
  useMarkFalsePositive,
  useResolveConflictAlert,
  useReopenConflictAlert,
} from '../../hooks/use-conflict-alert-mutations';
import {
  useConflictAlertById,
  useConflictAlertChain,
  useConflictAlertHistoryInfinite,
  useConflictAlertMcpBlockEvents,
  useConflictAlertMetrics,
  useConflictAlerts,
} from '../../hooks/use-conflict-alerts';
import { SEVERITY_LABELS } from '../../labels';

import { CentroAlertasDrawer } from './CentroAlertasDrawer';
import { MetricsTab } from './MetricsTab';
import { SeverityGroup } from './SeverityGroup';
import {
  adaptChainToSteps,
  adaptConflictAlertToVM,
  adaptConflictAlertsToVM,
  adaptHistoryRows,
  adaptMcpBlockEvents,
} from './tela7-adapter';
import { adaptMetrics } from './tela7-metrics';

import type { ConflictAlertRaw } from '../../types';
import type {
  ConflictAlertHistoryRowVM,
  ConflictAlertVM,
  ConflictSeverityVM,
  SupervisorVariant,
} from '../../view-models';
import type React from 'react';

// Rótulos de SLA de mitigação por severidade (verbatim do mockup F.conflictAlertSlas).
const SLA_LABELS: Record<ConflictSeverityVM, string> = {
  critical: '30min',
  high: '2h',
  medium: '24h',
  low: '7d',
};

type Tab = 'active' | 'history' | 'metrics';

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

// Cap de break-glass para resolver/revogar (Regulated opt-in). O botão "Resolver"
// no drawer só aparece se presente (gating spec §2.1; servidor revalida).
const CAP_RESOLVE = 'conflict-alert-resolve-or-revoke';

// Item 323 — cap canônica de leitura de Pessoas (a mesma que gateia o item "Pessoas"
// do menu). Presente → o "Ver Pessoa →" do drawer salta p/ a ficha /pessoas/:id.
const CAP_PERSON_READ = 'person-read';

// Item 335 — cap canônica de leitura de auditoria (a mesma que gateia o "Explorador
// de auditoria" do menu, /admin/audit). Presente → o "Ver audit completo →" do drawer
// (seção só-regulated) salta p/ o Explorador filtrado por este ConflictAlert.
const CAP_AUDIT_READ = 'audit-events-read';

// Categoria do conflito (id do radio do AcceptModal → label PT). Prefixada na
// justificativa porque o DTO de accept não tem campo `category` (spec §4.7).
const CATEGORY_PT: Record<string, string> = {
  familiar: 'Relação familiar declarada',
  profissional: 'Relação profissional pré-existente',
  identidade: 'Compartilhamento de identidade técnica (CPF/email)',
  outro: 'Outro',
};

function pushToast(variant: 'success' | 'error' | 'info', title: string): void {
  useToastStore.getState().push({ variant, title });
}

export function CentroAlertasPage(): React.JSX.Element {
  const { data, isLoading, isError } = useConflictAlerts();
  // Histórico paginado (cursor) — tab Histórico. Sempre montado (sem fetch extra
  // p/ a tab ativa muda só o render); filtros visuais ainda não cabeados ao hook.
  const history = useConflictAlertHistoryInfinite({});
  // Métricas diárias agregadas (tab Métricas — T7-5). Sempre montado; o adapter
  // soma os dias + cruza com a contagem de ativos da lista (active.length).
  const metrics = useConflictAlertMetrics();
  const { data: presence } = usePresence();
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canResolve = useCapability(CAP_RESOLVE);
  const canReadPerson = useCapability(CAP_PERSON_READ);
  const canReadAudit = useCapability(CAP_AUDIT_READ);
  // A-24 (noturna 04/Set/2026): a LISTA (`GET /conflict-alerts/v2`) exige esta cap; o menu abre a
  // tela por `conflict-alert-metrics-read`. Sem ela o backend devolve 403 e a tela dizia «Erro».
  const canListAlerts = useCapability('conflict-of-interest-detection');
  const navigate = useNavigate();

  // Item 323 — callback de navegação p/ a ficha da Pessoa, passado ao drawer SÓ
  // quando o caller tem person-read (gating por presença, como o onResolve). O drawer
  // ainda bloqueia parties redatadas (id mascarado) mesmo com o callback presente.
  const openPerson = canReadPerson
    ? (personId: string): void => navigate(`/pessoas/${personId}`)
    : undefined;

  // Item 335 — callback de navegação p/ o Explorador de auditoria filtrado por este
  // ConflictAlert, passado ao drawer SÓ quando o caller tem audit-events-read (gating
  // por presença). O backend filtra por entityType=conflict_alert + entityId=<alert.id>
  // (R15: os eventos do conflito gravam exatamente esse par — não há filtro por operationId).
  const openAudit = canReadAudit
    ? (alertId: string): void =>
        navigate(`/admin/audit?entityType=conflict_alert&entityId=${alertId}`)
    : undefined;

  const mitigate = useMitigateConflictAlert();
  const accept = useAcceptConflictAlert();
  const markFp = useMarkFalsePositive();
  const resolve = useResolveConflictAlert();
  const reopen = useReopenConflictAlert();

  const [tab, setTab] = useState<Tab>('active');
  const [drawerAlert, setDrawerAlert] = useState<ConflictAlertVM | null>(null);
  // T7-4 — id do alerta terminal cujo detalhe (drawer read-only) o Histórico abre.
  const [historyDetailId, setHistoryDetailId] = useState<string | null>(null);
  const [mitigateAlert, setMitigateAlert] = useState<ConflictAlertVM | null>(null);
  const [acceptAlert, setAcceptAlert] = useState<ConflictAlertVM | null>(null);
  const [fpAlert, setFpAlert] = useState<ConflictAlertVM | null>(null);
  const [resolveAlert, setResolveAlert] = useState<ConflictAlertVM | null>(null);
  // Reopen (Item 322 a) — guarda o raw do Histórico (carrega id + version p/ If-Match).
  const [reopenAlert, setReopenAlert] = useState<ConflictAlertRaw | null>(null);

  // Fecha o modal aberto + o drawer no sucesso da mutation (a lista é
  // invalidada pelos próprios hooks).
  const closeAll = (): void => {
    setMitigateAlert(null);
    setAcceptAlert(null);
    setFpAlert(null);
    setResolveAlert(null);
    setDrawerAlert(null);
  };

  // Presence carrega o nome real do User (Engine); sem resolução → '' (o adapter
  // degrada p/ rótulo honesto, nunca id cru).
  const nameFor = (userId: string): string =>
    presence?.find((p) => p.userId === userId)?.name ?? '';

  // T7-4 — detalhe do alerta terminal aberto pelo Histórico. Fetch gateado por
  // id != null (o GET /:id redacta parties sem `audit-events-read-sensitive`).
  const { data: historyDetailRaw } = useConflictAlertById(historyDetailId);
  const historyDetailVM: ConflictAlertVM | null =
    historyDetailRaw !== undefined ? adaptConflictAlertToVM(historyDetailRaw, nameFor) : null;

  // Alerta cujo drawer está aberto: aba Ativos (drawerAlert, com ações de lifecycle)
  // OU aba Histórico (historyDetailVM, modo leitura). Ativos tem prioridade se ambos
  // setados. drawerReadOnly distingue a fonte p/ esconder o rodapé de ações.
  const openAlert: ConflictAlertVM | null = drawerAlert ?? historyDetailVM;
  const drawerReadOnly = drawerAlert === null && historyDetailVM !== null;

  // Enriquecimento lazy do drawer: chain (severity/status real por step) + MCP block events.
  // A chain é buscada pela ORIGEM (chainOriginId), não pelo id do alerta aberto — senão
  // alertas não-origem (posição ≥2) carregam só a si mesmos e a timeline de reincidência some.
  const { data: chainData } = useConflictAlertChain(
    openAlert?.chain.chainOriginId ?? openAlert?.id ?? null,
  );
  const { data: mcpData } = useConflictAlertMcpBlockEvents(openAlert?.id ?? null);

  return (
    <CapabilityGate capability="conflict-alert-metrics-read" fallback={<SoloUpgradeCta />}>
      {!canListAlerts && <CapabilityLockedBanner capability="conflict-of-interest-detection" />}
      {canListAlerts && isLoading && <div aria-busy="true">Carregando Centro de Alertas…</div>}
      {canListAlerts && isError && !isLoading && (
        <div role="alert">Não foi possível carregar os alertas agora. Tente atualizar.</div>
      )}
      {canListAlerts &&
        data !== undefined &&
        !isLoading &&
        (() => {
          const active = adaptConflictAlertsToVM(data.data, nameFor);
          const bySev = (sev: ConflictSeverityVM): ConflictAlertVM[] =>
            active.filter((a) => a.severity === sev);
          const totalCritical = bySev('critical').length;

          // VM enriquecido do drawer: chain real (cada raw da chain com severity/status
          // próprios) + blockedMcpToolCalls via /mcp-block-events. Fallback = VM base.
          const enrichedDrawer: ConflictAlertVM | null =
            openAlert === null
              ? null
              : (() => {
                  const chain = chainData?.data ?? [];
                  const steps =
                    chain.length > 0 ? adaptChainToSteps(chain) : openAlert.chain.history;
                  // posição = índice (1-based) do step atual na chain; se não achar, fim da chain.
                  const idxInChain = steps.findIndex((s) => s.id === openAlert.id);
                  const position =
                    steps.length === 0
                      ? openAlert.chain.position
                      : idxInChain >= 0
                        ? idxInChain + 1
                        : steps.length;
                  const blocked =
                    mcpData?.data !== undefined
                      ? adaptMcpBlockEvents(mcpData.data)
                      : openAlert.impact.blockedMcpToolCalls;
                  return {
                    ...openAlert,
                    chain: {
                      position,
                      total: steps.length > 0 ? steps.length : openAlert.chain.total,
                      history: steps,
                    },
                    impact: { ...openAlert.impact, blockedMcpToolCalls: blocked },
                  };
                })();

          return (
            <>
              {variant === 'regulated' && (
                <div className="banner banner--regulated">
                  <span className="banner__icon" aria-hidden="true">
                    🛡
                  </span>
                  <div className="banner__body">
                    <span className="banner__title">Modo Regulado</span>
                    <span className="banner__sub">
                      {' · '}mitigation e accept obrigam documentação · operationId visível ·
                      DocumentationGated ativo
                    </span>
                  </div>
                </div>
              )}

              {totalCritical > 0 && (
                <div className="banner banner--danger">
                  <span className="banner__icon" aria-hidden="true">
                    🚨
                  </span>
                  <div className="banner__body">
                    <span className="banner__title">
                      {totalCritical} ConflictAlert CRÍTICO ativo{totalCritical === 1 ? '' : 's'}
                    </span>
                    <span className="banner__sub">
                      {' · '}ação imediata · a cascata de severidade alta+ pode estar pausando
                      conversas e bloqueando ferramentas MCP
                    </span>
                  </div>
                </div>
              )}

              <PageHead
                title="Centro de Alertas"
                subtitle={`ConflictAlert em tempo real · ${active.length} ativos`}
                filters={
                  <>
                    <div className="tab-row" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'active'}
                        className={
                          'tab-row__btn' + (tab === 'active' ? ' tab-row__btn--active' : '')
                        }
                        onClick={() => {
                          setTab('active');
                        }}
                      >
                        Ativos
                        <span className="tab-row__count">{active.length}</span>
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'history'}
                        className={
                          'tab-row__btn' + (tab === 'history' ? ' tab-row__btn--active' : '')
                        }
                        onClick={() => {
                          setTab('history');
                        }}
                      >
                        Histórico
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'metrics'}
                        className={
                          'tab-row__btn' + (tab === 'metrics' ? ' tab-row__btn--active' : '')
                        }
                        onClick={() => {
                          setTab('metrics');
                        }}
                      >
                        Métricas
                      </button>
                    </div>
                    <span style={{ width: 12 }} />
                    <button type="button" className="chip">
                      Tipo: todos <span className="chip__caret">▾</span>
                    </button>
                    <button type="button" className="chip">
                      Severidade: todas <span className="chip__caret">▾</span>
                    </button>
                    {tab === 'active' && (
                      <button type="button" className="chip chip--active">
                        Status: ativos <span className="chip__caret">▾</span>
                      </button>
                    )}
                    <button type="button" className="chip">
                      Período: 90d <span className="chip__caret">▾</span>
                    </button>
                  </>
                }
              />

              {tab === 'active' && (
                <div
                  className={
                    'tela7-body' + (drawerAlert !== null ? ' tela7-body--drawer-open' : '')
                  }
                >
                  <div>
                    {active.length === 0 ? (
                      <EmptyState />
                    ) : (
                      (['critical', 'high', 'medium', 'low'] as ConflictSeverityVM[]).map((sev) => (
                        <SeverityGroup
                          key={sev}
                          label={SEVERITY_LABELS[sev].toUpperCase()}
                          sev={sev}
                          alerts={bySev(sev)}
                          sla={SLA_LABELS[sev]}
                          defaultOpen={sev === 'critical' || sev === 'high'}
                          renderCard={(a) => (
                            <ConflictAlertCard
                              key={a.id}
                              alert={a}
                              onOpenDrawer={(vm) => {
                                // Abre o drawer com ações; fecha o de leitura do Histórico.
                                setHistoryDetailId(null);
                                setDrawerAlert(vm);
                              }}
                              onMitigate={setMitigateAlert}
                              onAccept={setAcceptAlert}
                            />
                          )}
                        />
                      ))
                    )}
                  </div>

                  {enrichedDrawer !== null && !drawerReadOnly && (
                    <div className="ca-drawer-inline">
                      <CentroAlertasDrawer
                        alert={enrichedDrawer}
                        variant={variant}
                        onClose={() => {
                          setDrawerAlert(null);
                        }}
                        onMitigate={() => {
                          setMitigateAlert(enrichedDrawer);
                        }}
                        onAccept={() => {
                          setAcceptAlert(enrichedDrawer);
                        }}
                        onFalsePositive={() => {
                          setFpAlert(enrichedDrawer);
                        }}
                        onResolve={
                          canResolve
                            ? () => {
                                setResolveAlert(enrichedDrawer);
                              }
                            : undefined
                        }
                        onOpenPerson={openPerson}
                        onOpenAudit={openAudit}
                      />
                    </div>
                  )}
                </div>
              )}

              {tab === 'history' &&
                (() => {
                  const rows = adaptHistoryRows(history.rows, nameFor);
                  // Split lista || drawer read-only quando o detalhe do Histórico está
                  // aberto — reusa o mesmo layout da aba Ativos (tela7-body--drawer-open).
                  const showDetailDrawer = drawerReadOnly && enrichedDrawer !== null;
                  return (
                    <div
                      className={
                        'tela7-body' + (showDetailDrawer ? ' tela7-body--drawer-open' : '')
                      }
                    >
                      <div>
                        {history.isError ? (
                          <div role="alert">Erro ao carregar o histórico.</div>
                        ) : history.isLoading ? (
                          <div aria-busy="true" style={{ color: 'var(--fg-3)' }}>
                            Carregando histórico…
                          </div>
                        ) : rows.length === 0 ? (
                          <HistoryEmptyState />
                        ) : (
                          <>
                            <HistorySummary rows={rows} />
                            <div className="ca-hist-list">
                              {rows.map((r) => (
                                <ConflictAlertHistoryRow
                                  key={r.id}
                                  row={r}
                                  onOpenDrawer={(row) => {
                                    // Busca o detalhe rico por id (fetch gateado) e abre o
                                    // drawer em modo leitura — sem ações de lifecycle.
                                    setDrawerAlert(null);
                                    setHistoryDetailId(row.id);
                                  }}
                                  onReopen={(vm) => {
                                    // O VM do Histórico não carrega version; busca o raw
                                    // da página corrente por id p/ o If-Match do reopen.
                                    setReopenAlert(
                                      history.rows.find((raw) => raw.id === vm.id) ?? null,
                                    );
                                  }}
                                />
                              ))}
                            </div>
                            {history.hasNextPage && (
                              <div style={{ textAlign: 'center', marginTop: 'var(--space-4)' }}>
                                <button
                                  type="button"
                                  className="btn-ghost-mini"
                                  disabled={history.isFetchingNextPage}
                                  onClick={() => {
                                    history.fetchNextPage();
                                  }}
                                >
                                  {history.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {showDetailDrawer && (
                        <div className="ca-drawer-inline">
                          <CentroAlertasDrawer
                            alert={enrichedDrawer}
                            variant={variant}
                            readOnly
                            onClose={() => {
                              setHistoryDetailId(null);
                            }}
                            onOpenPerson={openPerson}
                            onOpenAudit={openAudit}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

              {tab === 'metrics' && (
                <div style={{ padding: 'var(--space-5)' }}>
                  {metrics.isError ? (
                    <div role="alert">Erro ao carregar as métricas de conflict alerts.</div>
                  ) : metrics.isLoading ? (
                    <div aria-busy="true" style={{ color: 'var(--fg-3)' }}>
                      Carregando métricas…
                    </div>
                  ) : metrics.data === undefined || metrics.data.data.length === 0 ? (
                    <MetricsEmptyState />
                  ) : (
                    <MetricsTab metrics={adaptMetrics(metrics.data.data, active.length)} />
                  )}
                </div>
              )}

              {/* Modais de ação — T7-3 liga as mutations reais (optimistic concurrency via version). */}
              <MitigateAlertModal
                open={mitigateAlert !== null}
                alert={mitigateAlert}
                variant={variant}
                bondId={mitigateAlert?.bondsInvolved[0]}
                submitting={mitigate.isPending}
                onClose={() => {
                  setMitigateAlert(null);
                }}
                onConfirm={({ type, desc, evidence, notif }) => {
                  if (mitigateAlert === null) return;
                  mitigate.mutate(
                    {
                      id: mitigateAlert.id,
                      version: mitigateAlert.version,
                      body: {
                        mitigationType: type,
                        mitigationDescription: desc,
                        // Parte B: IDs reais dos Documents anexados via upload.
                        evidenceDocumentIds: evidence.map((e) => e.id),
                        notifyOperator: notif.op,
                        notifyClient: notif.cli,
                        notifyManager: notif.gestor,
                      },
                    },
                    {
                      onSuccess: () => {
                        pushToast('success', '✓ Conflito mitigado');
                        closeAll();
                      },
                      onError: () => pushToast('error', 'Falha ao mitigar o conflito'),
                    },
                  );
                }}
              />
              <AcceptAlertWithJustificationModal
                open={acceptAlert !== null}
                alert={acceptAlert}
                variant={variant}
                bondId={acceptAlert?.bondsInvolved[0]}
                submitting={accept.isPending}
                onClose={() => {
                  setAcceptAlert(null);
                }}
                onConfirm={({ justif, acceptType, ttlDays, evidence, category }) => {
                  if (acceptAlert === null) return;
                  const categoryLabel = CATEGORY_PT[category] ?? category;
                  accept.mutate(
                    {
                      id: acceptAlert.id,
                      version: acceptAlert.version,
                      body: {
                        justification: `[${categoryLabel}] ${justif}`,
                        acceptanceType: acceptType === 'temp' ? 'temporary' : 'permanent',
                        ...(acceptType === 'temp' ? { durationDays: ttlDays } : {}),
                        // Parte B: IDs reais dos Documents anexados via upload.
                        documentationDocumentIds: evidence.map((e) => e.id),
                      },
                    },
                    {
                      // Usa a verdade do servidor (dualApprovalPending), não o palpite do
                      // cliente (chain pos>=3) — o backend é quem decide aprovação dupla (review T7).
                      onSuccess: (data) => {
                        if (data.dualApprovalPending === true) {
                          pushToast('info', '→ Enviado para aprovação dupla (supervisor + gestor)');
                        } else {
                          pushToast('success', '✓ Conflito aceito com justificativa');
                        }
                        closeAll();
                      },
                      onError: () => pushToast('error', 'Falha ao aceitar o conflito'),
                    },
                  );
                }}
              />
              <FalsePositiveModal
                open={fpAlert !== null}
                alert={fpAlert}
                submitting={markFp.isPending}
                onClose={() => {
                  setFpAlert(null);
                }}
                onConfirm={({ reason }) => {
                  if (fpAlert === null) return;
                  markFp.mutate(
                    { id: fpAlert.id, version: fpAlert.version, body: { feedback: reason } },
                    {
                      onSuccess: () => {
                        pushToast('success', '✓ Marcado como falso positivo');
                        closeAll();
                      },
                      onError: () => pushToast('error', 'Falha ao marcar falso positivo'),
                    },
                  );
                }}
              />
              <ResolveModal
                open={resolveAlert !== null}
                alert={resolveAlert}
                submitting={resolve.isPending}
                onClose={() => {
                  setResolveAlert(null);
                }}
                onConfirm={({ reason }) => {
                  if (resolveAlert === null) return;
                  resolve.mutate(
                    { id: resolveAlert.id, version: resolveAlert.version, body: { reason } },
                    {
                      onSuccess: () => {
                        pushToast('success', '✓ Conflito resolvido');
                        closeAll();
                      },
                      onError: () => pushToast('error', 'Falha ao resolver o conflito'),
                    },
                  );
                }}
              />
              {/* Re-abrir (Item 322 a) — alerta terminal → volta a Ativos com flag Reopened. */}
              <ReopenAlertModal
                open={reopenAlert !== null}
                alert={reopenAlert}
                onClose={() => {
                  setReopenAlert(null);
                }}
                onConfirm={({ reason }) => {
                  if (reopenAlert === null) return;
                  reopen.mutate(
                    { id: reopenAlert.id, version: reopenAlert.version, body: { reason } },
                    {
                      onSuccess: () => {
                        pushToast('success', '↺ Alerta reaberto · voltou para Ativos');
                        setReopenAlert(null);
                        // Move o foco para a aba Ativos, onde o alerta reaberto aparece.
                        setTab('active');
                      },
                      onError: () => pushToast('error', 'Falha ao reabrir o alerta'),
                    },
                  );
                }}
              />
            </>
          );
        })()}
    </CapabilityGate>
  );
}

// Linha-resumo do histórico: contagem por status com label PT (resumo da página
// carregada — não totais globais, que exigiriam agregação no backend).
function HistorySummary({ rows }: { rows: ConflictAlertHistoryRowVM[] }): React.JSX.Element {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const key = r.resolution.label;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const parts = [...counts.entries()].map(([label, n]) => `${n} ${label.toLowerCase()}`);
  return (
    <div
      style={{
        color: 'var(--fg-3)',
        fontSize: 'var(--text-sm)',
        marginBottom: 'var(--space-4)',
      }}
    >
      {rows.length} no histórico{parts.length > 0 ? ` · ${parts.join(' · ')}` : ''}
    </div>
  );
}

function MetricsEmptyState(): React.JSX.Element {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px dashed var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-10) var(--space-6)',
        textAlign: 'center',
        color: 'var(--fg-3)',
      }}
    >
      Sem métricas de conflict alerts no período.
    </div>
  );
}

function HistoryEmptyState(): React.JSX.Element {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px dashed var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-10) var(--space-6)',
        textAlign: 'center',
        color: 'var(--fg-3)',
      }}
    >
      Nenhum conflict alert no histórico.
    </div>
  );
}

// EmptyState honesto: sem "última detecção há 2 dias" hardcoded (Item 317 — placeholder honesto).
function EmptyState(): React.JSX.Element {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px dashed var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-10) var(--space-6)',
        textAlign: 'center',
        color: 'var(--fg-3)',
      }}
    >
      <div style={{ fontSize: 'var(--text-3xl)', marginBottom: 12 }}>✓</div>
      <div
        style={{
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--fg-1)',
        }}
      >
        Nenhum conflict alert ativo no momento.
      </div>
    </div>
  );
}

function SoloUpgradeCta(): React.JSX.Element {
  return (
    <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
      <div style={{ fontSize: 'var(--text-3xl)', marginBottom: 12 }}>🔒</div>
      <h2 style={{ fontSize: 'var(--text-xl)', color: 'var(--fg-1)', marginBottom: 8 }}>
        Centro de Alertas requer Professional+
      </h2>
      <p style={{ maxWidth: 480, margin: '0 auto', fontSize: 'var(--text-sm)' }}>
        No pacote Solo, a detecção de conflitos de interesse, achados de auditoria e cascata de
        pausa H_c + bloqueio MCP não está disponível. Faça upgrade para Team-Pro ou Regulated.
      </p>
    </div>
  );
}
